<template>
  <Teleport to="body">
    <Modal v-model="show">
      <ModalContent size="full">
        <template #header>
          <Heading level="h2">{{ title }}</Heading>
        </template>

        <div class="tw-space-y-3">
          <div class="tw-rounded-[16px] tw-border tw-border-white/10 tw-bg-black/30">
            <div v-if="initialLoading" class="tw-flex tw-items-center tw-gap-3 tw-p-4">
              <Spinner size="small" />
              <Text>Loading logs…</Text>
            </div>

            <Text v-else-if="errorText" class="tw-m-4 tw-text-state-err">
              {{ errorText }}
            </Text>

            <pre
              v-else
              ref="logPreRef"
              class="tw-h-[calc(100vh-220px)] tw-overflow-auto tw-whitespace-pre tw-p-4 tw-font-mono tw-text-sm tw-leading-5"
            ><template v-for="row in rowsUi" :key="row.key"><span :class="row.className" class="tw-block">{{ row.text }}</span></template></pre>
          </div>
        </div>

        <template #footer="{ reject }">
          <div class="tw-flex tw-justify-end tw-gap-2">
            <Button
              color="semiTransparentWhite"
              outlined
              :loading="loadTail === MAX_ROWS"
              :disabled="loadTail === MAX_ROWS"
              @click="loadTail = MAX_ROWS"
              >Load {{ MAX_ROWS.toLocaleString('en-US') }} lines</Button
            >
            <Button
              tag="a"
              :href="snapshotUrl"
              target="_blank"
              rel="noopener noreferrer"
              color="semiTransparentWhite"
              outlined
              >Download snapshot</Button
            >
            <Button color="transparent" outlined @click="reject">Close</Button>
          </div>
        </template>
      </ModalContent>
    </Modal>
  </Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, nextTick, ref, watch } from 'vue';

import { Button, Heading, Modal, ModalContent, Spinner, Text } from '@slideslive/fuse-kit/vue';

import polling from '@/composables/polling';
import scrollAnchor from '@/composables/scroll_anchor';
import fetcher from '@/fetcher';
import useStateStore from '@/stores/state';
import { findOverlap, formatRowText, normalizeLevel, parseLogRowsFromResponse, rowKey } from '@/utils/log_rows';

const MAX_ROWS = 10000;
const INITIAL_TAIL = 1000;
const POLL_TAIL = 100;

const show = defineModel();

const { kind } = defineProps({
  kind: { type: String, required: true },
});

const title = computed(() => (kind === 'runner' ? 'NATE Runner logs' : 'NATE logs'));
const endpoint = computed(() => (kind === 'runner' ? '/logs/runner' : '/logs/nate'));
const snapshotUrl = computed(() =>
  fetcher.buildUrl(`${kind === 'runner' ? '/logs/runner' : '/logs/nate'}/-/pretty?tail=${MAX_ROWS}`),
);

const stateStore = useStateStore();
const { sessionId } = storeToRefs(stateStore);

const rows = ref([]);
const logPreRef = ref(null);
const { getState: getScrollState, restore: restoreScroll } = scrollAnchor(logPreRef);
// Lines of the next fetch that replaces the rows, null when polling appends new rows.
const loadTail = ref(INITIAL_TAIL);

const enabled = computed(() => Boolean(show.value) && Boolean(sessionId.value));

const poll = polling({
  enabled,
  refetchOnWindowFocus: ref(true),
  retryCount: ref(100),
  queryFn: async (_unused, { abortController }) => {
    const replaceTail = loadTail.value;
    const res = await fetcher.get(
      endpoint.value,
      {
        tail: replaceTail ?? POLL_TAIL,
        reverse: false,
        session_id: sessionId.value,
      },
      { abortController },
    );

    return res && { res, replaceTail };
  },
});

const initialLoading = computed(
  () => Boolean(show.value) && Boolean(sessionId.value) && !poll.loaded.value && poll.fetching.value,
);

const errorText = computed(() => {
  const e = poll.error.value;
  if (!e) return null;
  return e?.message || String(e);
});

const rowsUi = computed(() =>
  rows.value.map((r, idx) => {
    const level = normalizeLevel(r?.level);

    return {
      key: `${idx}-${rowKey(r)}`,
      text: formatRowText(r, level),
      className: level === 'error' ? 'tw-text-state-err' : level === 'warn' ? 'tw-text-state-warn' : '',
    };
  }),
);

function appendNewRows(incoming) {
  if (!rows.value.length) {
    rows.value = incoming.slice(-MAX_ROWS);
    return;
  }

  const overlap = findOverlap(rows.value, incoming);
  const newPart = incoming.slice(overlap);

  if (!newPart.length) return;

  const next = rows.value.concat(newPart);
  rows.value = next.length > MAX_ROWS ? next.slice(next.length - MAX_ROWS) : next;
}

async function applyLogResponse({ res, replaceTail }) {
  // A click on the load button during this fetch asks for a bigger tail, so keep it for the next fetch.
  if (replaceTail != null && loadTail.value === replaceTail) loadTail.value = null;

  const incoming = parseLogRowsFromResponse(res);
  if (!incoming.length) return;

  const scroll = replaceTail != null ? { atBottom: true, scrollTop: 0 } : getScrollState();

  if (replaceTail != null) rows.value = incoming.slice(-MAX_ROWS);
  else appendNewRows(incoming);

  await nextTick();

  restoreScroll(scroll);
}

watch(
  () => poll.data.value,
  (data) => {
    if (!show.value) return;
    if (!data) return;
    applyLogResponse(data);
  },
  { immediate: true },
);

watch(
  show,
  (open) => {
    if (!open) return;
    rows.value = [];
    loadTail.value = INITIAL_TAIL;
    poll.error.value = null;
  },
  { immediate: true, flush: 'sync' },
);
</script>
